import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import ReporteVuelosPDF from '../../components/PDF/ReporteVuelosPDF';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FlightTakeoff as FlightIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ReporteVuelos = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fecha_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
    estado: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`${endpoints.reportes.vuelos}?${params}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FlightIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Reporte de Vuelos
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Historial detallado de vuelos con estadísticas de carga
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Fecha Inicio"
                type="date"
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Fecha Fin"
                type="date"
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  name="estado"
                  value={filters.estado}
                  onChange={handleFilterChange}
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="abierto">Abiertos</MenuItem>
                  <MenuItem value="cerrado">Cerrados</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resumen */}
      {data?.resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {data.resumen.total_vuelos}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Vuelos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {data.resumen.vuelos_cerrados}
              </Typography>
              <Typography variant="body2" color="text.secondary">Cerrados</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {data.resumen.vuelos_abiertos}
              </Typography>
              <Typography variant="body2" color="text.secondary">Abiertos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {data.resumen.total_pallets}
              </Typography>
              <Typography variant="body2" color="text.secondary">Pallets</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.50' }}>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {(data.resumen.peso_total / 1000).toFixed(1)}t
              </Typography>
              <Typography variant="body2" color="text.secondary">Peso Total</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Table */}
      <Card sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>AWB</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Equipo</TableCell>
                <TableCell align="center">Pallets</TableCell>
                <TableCell align="right">Peso (kg)</TableCell>
                <TableCell align="center">Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data?.vuelos?.length > 0 ? (
                data.vuelos.map((v) => (
                  <TableRow key={v.awb} hover>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/vuelos/${v.awb}/detalle`}
                        sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {v.awb}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(v.fecha).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip label={v.destino} size="small" />
                    </TableCell>
                    <TableCell>{v.equipo}</TableCell>
                    <TableCell align="center">{v.total_pallets}</TableCell>
                    <TableCell align="right">{(v.peso_total || 0).toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={v.cerrado ? 'Cerrado' : 'Abierto'}
                        color={v.cerrado ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay vuelos en el rango seleccionado</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {data && (
          <PDFDownloadLink
            document={<ReporteVuelosPDF data={data} filters={filters} />}
            fileName={`reporte-vuelos-${filters.fecha_inicio}-${filters.fecha_fin}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading: pdfLoading }) => (
              <Button
                variant="contained"
                color="success"
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                disabled={pdfLoading}
              >
                {pdfLoading ? 'Generando...' : 'Descargar PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        )}
        <Button component={Link} to="/reportes" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default ReporteVuelos;

